import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Label,
  Brush,
  Tooltip,
  Line,
  Bar,
  ComposedChart,
  Area,
} from "recharts";
import { isMobile, PrimaryColor, SecondaryColor } from "../../common";
import {
  BAR_RADIUS,
  BrushStart,
  ChartArguements,
  ChartType,
  LegendKey,
} from "../../models/chart_model";
import { COLORS } from "./ReCharts";
import { YAxisFormatter, renderQuarterTick } from "./components";
import { COST_UNIT } from "../../Units";
import { getTimeRange } from "../../pages/Dashboard/Exchange3/Chart";

const getLegendColor = (
  legend: LegendKey,
  index: number | string,
  selectedLegends: LegendKey[],
  isLegend?: boolean
): string => {
  const isSelected =
    selectedLegends.some((item) => item.dataKey === legend.dataKey) ||
    selectedLegends.length === 0;
  return isSelected
    ? typeof index === "string"
      ? index
      : (isLegend ? legend.legendColor : legend.stroke) ??
        legend.stroke ??
        COLORS[index % COLORS.length]
    : "#E0E0E0";
};

export function lightenColor(color: string, percent: number): string {
  const num = parseInt(color.replace("#", ""), 16),
    amt = Math.round(2.55 * percent),
    R = (num >> 16) + amt,
    G = ((num >> 8) & 0x00ff) + amt,
    B = (num & 0x0000ff) + amt;

  return `#${(
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  )
    .toString(16)
    .slice(1)}`;
}

export const LegendComponent = (
  legend: LegendKey,
  index: number | string,
  selectedLegends: LegendKey[],
  handleLegendClick: (legend: LegendKey) => void,
  circleOnly?: boolean,
  fontSize = 12
) => (
  <div
    key={legend.dataKey}
    className={
      legend.type == ChartType.Bar || circleOnly ? "bar-legend" : "line-legend"
    }
    style={{
      fontSize: fontSize.toString() + `px`,
    }}
    onClick={() => handleLegendClick(legend)}
  >
    <div
      style={{ color: getLegendColor(legend, index, selectedLegends, true) }}
    >
      <div
        className={
          legend.type === ChartType.Bar || circleOnly
            ? "dot"
            : legend.isDash
            ? "dash"
            : "line"
        }
        style={{
          marginRight: "6px",
          backgroundColor: getLegendColor(legend, index, selectedLegends, true),
          borderColor: getLegendColor(legend, index, selectedLegends, true),
        }}
      ></div>
      {legend.name}
    </div>
  </div>
);

export function ReMixChart({
  data,
  legends,
  onLegendSelect,
  syncid,
  unit = "",
  overflowLegend = false,
  xDataKey,
  xTick,
  xLabel,
  yAxisLabel,
  legendFontSize,
  secondYAxisLabel,
  yAxisWidth,
  legendBreakIndex,
  hidePrice,
  circleLegend = false,
  xAxisLabelPosition = "insideBottom",
  xAxisLabelOffset,
  onlyTitle = false,
  disableLegendClick = false,
  secondXDataKey,
  showBrush = false,
  brushIndex,
  labelFontSize,
  rendexSecondXTick,
  brushHeight = 8,
  brushStart = BrushStart.End,
  xaxisHeight = 50,
  secondYAxisWidth,
  secondXAxisHeight = 12,
  barCategoryGap = 2.5,
  xAxisTickMargin = 4,
  isTimeSlot,
  xAxisAngle,
  onBarClick,
  fontSize = 12,
  secondXAxisLabel,
  customTooltip,
  hiddenLegends = [],
  hiddenStats = [],
  dynamicMarginTop = "mt-2",
  chartHeight = "",
}: ChartArguements) {
  const [selectedLegends, setSelectedLegends] = useState<LegendKey[]>([]);

  const handleLegendClick = useCallback(
    (legend: LegendKey) => {
      if (disableLegendClick) return;

      setSelectedLegends((prevSelectedLegends) => {
        const newSelectedLegends = prevSelectedLegends.some(
          (item) => item.dataKey === legend.dataKey
        )
          ? prevSelectedLegends.filter(
              (item) => item.dataKey !== legend.dataKey
            )
          : [...prevSelectedLegends, legend];

        if (onLegendSelect) {
          onLegendSelect(newSelectedLegends);
        }

        return newSelectedLegends;
      });
    },
    [disableLegendClick, onLegendSelect]
  );

  useEffect(() => {
    if (
      selectedLegends.length === legends.length &&
      selectedLegends.length !== 0
    ) {
      setSelectedLegends([]);
    }
  }, [selectedLegends]);

  useEffect(() => {
    // console.log("Data Received", data);

    setSelectedLegends([]);
  }, [data]);

  const filteredLegends = useMemo(
    () => legends.filter((e) => e.dataKey !== xDataKey),
    [legends, xDataKey]
  );

  const brushStartIndex = useMemo(
    () =>
      data && brushStart === BrushStart.Start
        ? 0
        : data.length > 95
        ? data.length - 96
        : 0,
    [data, brushStart]
  );

  const brushEndIndex = useMemo(
    () =>
      data && brushStart === BrushStart.Start
        ? 95
        : data.length > 96
        ? data.length - 1
        : 0,
    [data, brushStart]
  );

  const orderedSelectedLegends = useMemo(() => {
    if (selectedLegends.length === 0) return filteredLegends;
    return legends.filter((legend) =>
      selectedLegends.some((selected) => selected.dataKey === legend.dataKey)
    );
  }, [selectedLegends, filteredLegends, legends]);

  if (isMobile) {
    barCategoryGap = barCategoryGap / 3;
    fontSize = fontSize - 5;
    yAxisWidth = 50;
    yAxisWidth = 50;
    secondYAxisWidth = 40;
    xAxisAngle = xAxisAngle ?? 20;
  }
  const legendKeyColor: { key: String; color: string }[] = legends.map(
    (legend, index) => {
      return {
        key: legend.dataKey,
        color: COLORS[index % COLORS.length],
      };
    }
  );
  return (
    <>
      <div
        style={{ height: chartHeight ? chartHeight : undefined }}
        className="chart-container"
      >
        <ResponsiveContainer>
          <ComposedChart
            barCategoryGap={barCategoryGap}
            syncId={syncid}
            data={data}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              tick={xTick}
              height={xaxisHeight}
              tickSize={9.3}
              angle={xAxisAngle}
              tickMargin={xAxisTickMargin}
              fontSize={fontSize}
              interval={xTick ? 0 : undefined}
              minTickGap={8}
              dataKey={xDataKey}
            >
              {
                <Label
                  fontSize={labelFontSize ?? fontSize + 3}
                  fill={SecondaryColor}
                  offset={xAxisLabelOffset}
                  value={xLabel}
                  // height={40}

                  position={xAxisLabelPosition}
                />
              }
            </XAxis>
            {secondXDataKey && (
              <XAxis
                dataKey={secondXDataKey}
                axisLine={false}
                tickLine={false}
                fontSize={fontSize}
                interval={0}
                tick={rendexSecondXTick ?? (renderQuarterTick as any)}
                height={secondXAxisHeight}
                xAxisId="quarter"
              >
                {secondXAxisLabel && (
                  <Label
                    fontSize={fontSize + 3}
                    fill={SecondaryColor}
                    value={secondXAxisLabel}
                    style={{ textAnchor: "middle" }}
                  />
                )}
              </XAxis>
            )}
            <YAxis
              tickFormatter={YAxisFormatter}
              fontSize={fontSize}
              width={yAxisWidth}
            >
              <Label
                fontSize={labelFontSize ?? fontSize + 3}
                fill={SecondaryColor}
                value={yAxisLabel}
                angle={-90}
                position="insideLeft"
                style={{ textAnchor: "middle" }}
              />
            </YAxis>
            <YAxis
              yAxisId="right"
              orientation="right"
              fontSize={fontSize}
              width={secondYAxisWidth}
              tickFormatter={YAxisFormatter}
            >
              <Label
                fontSize={labelFontSize ?? fontSize + 3}
                value={secondYAxisLabel}
                position="insideRight"
                fill={SecondaryColor}
                angle={-90}
                style={{ textAnchor: "middle" }}
              />
            </YAxis>

            {showBrush && (
              <Brush
                dataKey={xDataKey}
                height={brushHeight}
                stroke={"transparent"}
                fill="#FFF4ED"
                startIndex={brushIndex?.startIndex ?? brushStartIndex}
                endIndex={brushIndex?.endIndex ?? brushEndIndex}
                floodColor={PrimaryColor}
                travellerWidth={12}
              />
            )}

            {customTooltip ? (
              <Tooltip content={customTooltip} />
            ) : (
              <Tooltip
                contentStyle={{
                  fontSize: `${fontSize}px`,
                  fontWeight: 500,
                  borderRadius: "10px",
                  boxShadow: "0px 0px 10px 0px rgba(0,0,0,0.1)",
                  border: "none",
                }}
                labelStyle={{
                  fontSize: `${fontSize + 1}px`,
                  color: "#6F6D7C",
                }}
                labelFormatter={(value, payload) => {
                  try {
                    if (secondXDataKey && payload[0].payload[secondXDataKey]) {
                      if (isTimeSlot) {
                        // return [getTimeRange(parseInt(value)) + ` (${value})`];
                        return [
                          ` ${payload[0].payload[secondXDataKey]} - ${
                            payload[0].payload[xDataKey]
                          } (${getTimeRange(
                            parseInt(payload[0].payload[xDataKey])
                          )}) `,
                        ];
                      }
                      return [
                        ` ${payload[0].payload[secondXDataKey]} - ${payload[0].payload[xDataKey]} `,
                      ];
                    }
                    if (onlyTitle || isTimeSlot) {
                      return [value];
                    }
                    if (isTimeSlot) {
                      return [getTimeRange(parseInt(value)) + ` (${value})`];
                    }

                    if (!payload[0].payload.date) {
                      return [value];
                    }
                    return [value];
                  } catch {
                    // if (isTimeSlot) {
                    //   return [value, getTimeRange(parseInt(value))];
                    // }
                    // if (isTimeSlot) {
                    //   return [value, getTimeRange(parseInt(value))];
                    // }
                    return [value];
                  }
                }}
                formatter={(value, name) => {
                  const suffixDict = {
                    Price: COST_UNIT,
                    Percent: "%",

                    "%": "%",
                    Cost: "Rs",
                    Revenue: "Rs",
                  };

                  const legend = legends.find((legend) => legend.name === name);

                  if (legend?.unit != undefined) {
                    return `${parseFloat(value.toString())
                      .toFixed(2)
                      .toString()
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")} `;
                  }
                  const foundKey = Object.keys(suffixDict).find((key) =>
                    name?.toString().includes(key)
                  );
                  let suffix = foundKey
                    ? suffixDict[foundKey as keyof typeof suffixDict]
                    : unit;
                  let val = `${parseFloat(value.toString())
                    .toFixed(2)
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")} ${suffix}`;
                  if (hidePrice) {
                    return val.replace("Price", "");
                  }
                  return val;
                }}
              />
            )}

            {orderedSelectedLegends
              .filter((e) => !hiddenStats.includes(e.name || ""))
              .map((legend, index) =>
                legend.type === ChartType.Bar ? (
                  <Bar
                    key={index}
                    fill={
                      legend.fill
                        ? legend.fill
                        : legendKeyColor.find(
                            (item) => item.key === legend.dataKey
                          )?.color
                    }
                    radius={BAR_RADIUS}
                    unit={legend.unit}
                    onClick={onBarClick}
                    type="monotone"
                    name={legend.name}
                    stackId={legend.stackId}
                    dataKey={legend.dataKey}
                    yAxisId={legend.yAxisId}
                    stroke={
                      legend.stroke ??
                      legendKeyColor.find((item) => item.key === legend.dataKey)
                        ?.color
                    }
                    maxBarSize={80}
                  />
                ) : legend.type === ChartType.Area ? (
                  <Area
                    key={index}
                    type="monotone"
                    name={legend.name}
                    stackId={legend.stackId}
                    unit={legend.unit}
                    dataKey={legend.dataKey}
                    yAxisId={legend.yAxisId}
                    fill={
                      legend.fill ??
                      legend.stroke ??
                      legendKeyColor.find((item) => item.key === legend.dataKey)
                        ?.color
                    }
                    stroke={
                      legend.stroke ??
                      legend.fill ??
                      legendKeyColor.find((item) => item.key === legend.dataKey)
                        ?.color
                    }
                    strokeWidth={3}
                  />
                ) : (
                  <Line
                    key={index}
                    type="monotone"
                    name={legend.name}
                    yAxisId={legend.yAxisId}
                    unit={legend.unit}
                    dataKey={legend.dataKey}
                    stroke={
                      legend.stroke ??
                      legend.fill ??
                      legendKeyColor.find((item) => item.key === legend.dataKey)
                        ?.color
                    }
                    dot={false}
                    strokeWidth={2}
                    strokeDasharray={legend.isDash ? "5 5" : undefined}
                  />
                )
              )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div
        className={`flex flex-wrap overflow-legend justify-center gap-x-2 z-[99] ${
          dynamicMarginTop ? dynamicMarginTop : ""
        }`}
      >
        {filteredLegends.map((legend, index) => {
          const isHidden = hiddenLegends.includes(legend.name ?? "");

          return (
            <React.Fragment key={index}>
              <div className={`mb-2 ${isHidden ? "hidden" : ""}`}>
                {LegendComponent(
                  legend,
                  getLegendColor(legend, index, selectedLegends),
                  selectedLegends,
                  handleLegendClick,
                  circleLegend,
                  legendFontSize
                )}
              </div>
              {legendBreakIndex != 0 && legendBreakIndex === index && (
                <div style={{ width: "100%", height: "2px" }}></div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
}
